import { FC, useEffect } from "react";
import { useApp, ConnectionSessionErrorId } from "../../context/AppContext";
import isEmpty from "lodash/isEmpty";
import { PreviewInsoleRoute } from "../../constants/routes";
import { createNavigateWithParams } from "../../helpers/navigation";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "../../assets/css/preview.module.scss";
import awaitingConnection from '../../assets/images/spinner.png';
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { trackScanInProgressStart, trackScanComplete } from "../../services/google_analytics";
import UnavailableSizeNotice from "./UnavailableSizeNotice";
import ScanErrorMessageModal from "./ScanErrorMessageModal";
import { isFootSizeNotSold } from "../../helpers/foot";

const ScanInProgress: FC = () => {
  useScrollToTop();
  const { connectionInstruction,
    connectionCountdown,
    connectionScanningFoot,
    connectionProcessing,
    connectionSessionError,
    customInsoleValues: {
      leftFootSizeData,
      rightFootSizeData,
      leftFootLengthInMM,
      rightFootLengthInMM
    } } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const sessionErrorMessageId = connectionSessionError?.id;

  const areBothFeetScanned = leftFootLengthInMM > 0 && rightFootLengthInMM > 0;

  const areBothFeetSizeValid = (() => {
    return Boolean(leftFootSizeData) && !isEmpty(leftFootSizeData) && Boolean(rightFootSizeData) && !isEmpty(rightFootSizeData);
  })();

  const isAnyFootSizeNotSold = isFootSizeNotSold(leftFootLengthInMM) || isFootSizeNotSold(rightFootLengthInMM);

  useEffect(() => {
    trackScanInProgressStart();
  }, []);

  useEffect(() => {
    if (import.meta.env.VITE_STUB_DATA_FOR_DEVELOPMENT === 'true') {
      return;
    }
    if (areBothFeetScanned && areBothFeetSizeValid && !Boolean(connectionScanningFoot)) {
      trackScanComplete();
      createNavigateWithParams(navigate, searchParams)(PreviewInsoleRoute, { replace: true });
    }
  }, [areBothFeetScanned, areBothFeetSizeValid, connectionScanningFoot, navigate, searchParams, import.meta.env.VITE_STUB_DATA_FOR_DEVELOPMENT]);

  const renderFinalText = () => {
    if (connectionProcessing) {
      return (
        <div>
          <div className={styles.scanInProgressLoaderContainer}>
            <img src={awaitingConnection} alt="Superfeet Logo" className={styles.cardIconImage} />
          </div>
          <div className={styles.scanInProgressLoaderText}>
            Scan in progress
          </div>
        </div>
      );
    }

    if (connectionCountdown) {
      return (
        <div>
          <div className={styles.scanInProgressReadyToScan}>
            Ready to scan
          </div>
          <div className={styles.scanInProgressCountdown}>
            {connectionCountdown}
          </div>
        </div>
      );
    }

    return (
      <div className={styles.scanInProgressFootWarning}>{connectionInstruction}</div>
    );
  };

  if (sessionErrorMessageId == ConnectionSessionErrorId.FOOT_LENGTH_OUT_OF_RANGE ||
      (!Boolean(connectionScanningFoot) && areBothFeetScanned && isAnyFootSizeNotSold)) {
    return <UnavailableSizeNotice />;
  }

  return (
    <>
      <div className={styles.scanInProgressToggleSwitchContainer}>
        <div className={styles.toggleSwitch}>
          <input type="radio" id="left" name="activeFoot" value="left" checked={connectionScanningFoot === "left" || !Boolean(connectionScanningFoot)} />
          <label className={styles.cursorDefault} htmlFor="left">Left</label>
          <input type="radio" id="right" name="activeFoot" value="right" checked={connectionScanningFoot === "right"} />
          <label className={styles.cursorDefault} htmlFor="right">Right</label>
          <div className={styles.toggleSwitchslider}></div>
        </div>
      </div>
      <div className={styles.scanInProgressDataContainer}>
        <div className={styles.scanInProgressBgBox}>
          <div className={styles.scanInProgressData}>
            {renderFinalText()}
          </div>
        </div>
      </div>
      <ScanErrorMessageModal />
    </>
  );
};

export default ScanInProgress;
